import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiPrivate } from "@/lib/api";
import AppLayout from "@/components/layout/AppLayout";
import SurveyBanner from "@/components/survey/SurveyBanner";
import { ArrowUpRight, FileText, HelpCircle, User, Bell, Sparkles, CheckCircle2, AlertTriangle, ShieldCheck, Briefcase, Mail, TrendingUp } from "lucide-react";

function daysSince(iso) {
    if (!iso) return Infinity;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

const FIELD_LABELS = {
    pec: "PEC email",
    telefono: "Telefono",
    indirizzo: "Indirizzo / Sede",
    codice_fiscale: "Codice fiscale",
    data_nascita: "Data di nascita",
};

export default function DashboardSocio() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [docs, setDocs] = useState([]);
    const [notifs, setNotifs] = useState([]);
    const [onboardingState, setOnboardingState] = useState(null);
    const [disclaimerState, setDisclaimerState] = useState(null);
    const [myListings, setMyListings] = useState([]);
    const [holdings, setHoldings] = useState([]);

    useEffect(() => {
        // Check onboarding first; if not completed, redirect
        apiPrivate.get("/onboarding/state").then(r => {
            if (!r.data.onboarding_completed && user?.role === "socio") {
                navigate("/app/onboarding", { replace: true });
            }
            setOnboardingState(r.data);
        }).catch(() => {});
        Promise.all([
            apiPrivate.get("/documents").then(r => setDocs(r.data)).catch(() => {}),
            apiPrivate.get("/notifications").then(r => setNotifs(r.data)).catch(() => {}),
            apiPrivate.get("/market/disclaimer").then(r => setDisclaimerState(r.data)).catch(() => {}),
            apiPrivate.get("/market/my-listings").then(r => setMyListings(r.data)).catch(() => {}),
            apiPrivate.get("/soci/me/holdings").then(r => setHoldings(r.data)).catch(() => {}),
        ]);
    }, [navigate, user]);

    // Numero di quote = somma valore_nominale (1€ nominale = 1 quota) — con breakdown per categoria
    // Fallback: se `quote_holdings` è vuoto ma l'utente ha i campi legacy denormalizzati
    // (`valore_nominale_quota` + `quota_categories`), distribuisce il totale equamente tra le
    // categorie possedute. Necessario per utenti migrati non ancora normalizzati in `quote_holdings`.
    const quoteSummary = useMemo(() => {
        const perCat = { A: 0, B: 0, C: 0, D: 0 };
        for (const h of holdings) {
            const cat = (h.categoria || "").toUpperCase();
            if (cat in perCat) perCat[cat] += Number(h.valore_nominale) || 0;
        }
        let total = perCat.A + perCat.B + perCat.C + perCat.D;
        if (total === 0 && user?.valore_nominale_quota) {
            total = Number(user.valore_nominale_quota) || 0;
            const cats = user?.quota_categories || [];
            if (cats.length > 0 && total > 0) {
                const perCatVal = total / cats.length;
                cats.forEach(c => {
                    const upper = String(c || "").toUpperCase();
                    if (upper in perCat) perCat[upper] = perCatVal;
                });
            }
        }
        const breakdown = Object.entries(perCat).filter(([, v]) => v > 0);
        return { total, breakdown };
    }, [holdings, user]);

    const unreadDocs = useMemo(() => docs.filter(d => !d.is_read).length, [docs]);
    const unreadNotifs = useMemo(() => notifs.filter(n => !n.read).length, [notifs]);
    const staleData = daysSince(user?.last_data_update) > 180;
    const displayName = user?.nome ? `${user.nome}` : (user?.ragione_sociale || "Socio");
    const hasQuota = (user?.quota_categories || []).length > 0;

    // Aggregated action items
    const actionItems = useMemo(() => {
        const items = [];
        if (onboardingState && onboardingState.completion_percent < 100) {
            const missing = [
                ...(onboardingState.missing_contact_fields || []),
                ...(onboardingState.missing_identity_fields || []),
            ];
            items.push({
                id: "profile",
                priority: missing.length > 2 ? "high" : "medium",
                icon: User,
                title: "Completa il tuo profilo",
                description: missing.length > 0
                    ? `Mancano: ${missing.map(f => FIELD_LABELS[f] || f).join(", ")}`
                    : "Profilo quasi completo",
                percent: onboardingState.completion_percent,
                cta: "Aggiorna profilo",
                to: "/app/profilo",
                testid: "action-profile",
            });
        }
        if (staleData) {
            items.push({
                id: "stale",
                priority: "high",
                icon: AlertTriangle,
                title: "Conferma i tuoi dati anagrafici",
                description: "Sono passati più di 6 mesi dall'ultimo aggiornamento. Verifica che siano ancora corretti.",
                cta: "Verifica ora",
                to: "/app/profilo",
                testid: "action-stale",
            });
        }
        if (unreadDocs > 0) {
            items.push({
                id: "docs",
                priority: "medium",
                icon: FileText,
                title: unreadDocs === 1 ? "1 documento non letto" : `${unreadDocs} documenti non letti`,
                description: "Verbali, comunicazioni e certificati a te destinati attendono la lettura.",
                cta: "Vai ai documenti",
                to: "/app/documenti",
                testid: "action-docs",
            });
        }
        if (unreadNotifs > 0) {
            items.push({
                id: "notifs",
                priority: "low",
                icon: Bell,
                title: unreadNotifs === 1 ? "1 notifica nuova" : `${unreadNotifs} notifiche nuove`,
                description: "Aggiornamenti dalla società e nuove comunicazioni di tuo interesse.",
                cta: "Apri notifiche",
                to: "/app/notifiche",
                testid: "action-notifs",
            });
        }
        if (hasQuota && disclaimerState && !disclaimerState.accepted) {
            items.push({
                id: "market-disclaimer",
                priority: "low",
                icon: Briefcase,
                title: "Accedi alla Bacheca annunci soci",
                description: "Per acquistare o cedere quote tra soci, è necessario accettare il disclaimer informativo.",
                cta: "Vai alla Bacheca",
                to: "/app/mercato",
                testid: "action-market",
            });
        }
        // Sort by priority
        const order = { high: 0, medium: 1, low: 2 };
        return items.sort((a, b) => order[a.priority] - order[b.priority]);
    }, [onboardingState, staleData, unreadDocs, unreadNotifs, hasQuota, disclaimerState]);

    return (
        <AppLayout data-testid="dashboard-socio">
            <div className="max-w-[1400px] mx-auto" data-testid="dashboard-socio-content">
                <span className="label-tag">Dashboard</span>
                <h1 className="font-display text-5xl lg:text-7xl mt-3 tracking-tighter" data-testid="dashboard-greeting">
                    Ciao, <span className="font-serif-italic text-gradient-tropical">{displayName}.</span>
                </h1>
                <p className="text-zinc-400 mt-4 max-w-xl">Il tuo riepilogo soci in tempo reale. Aggiornamenti, documenti non letti e azioni da fare.</p>

                {/* Action items */}
                <ActionItemsPanel items={actionItems} />

                {/* Active survey banner (non-blocking) */}
                <SurveyBanner />

                {/* KPI */}
                <div className="grid grid-cols-12 gap-4 mt-10">
                    <KpiBig
                        title="Numero quote"
                        value={quoteSummary.total > 0 ? quoteSummary.total.toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"}
                        sub={quoteSummary.total > 0 ? "Totale quote possedute" : ""}
                        breakdown={quoteSummary.breakdown}
                        testid="kpi-valore"
                    />
                    <KpiSmall icon={FileText} title="Documenti non letti" value={unreadDocs} color="bayside" testid="kpi-docs" />
                    <KpiSmall icon={Bell} title="Notifiche nuove" value={unreadNotifs} color="magenta" testid="kpi-notifs" />
                </div>

                {/* Badge: inserzioni attive sulla Bacheca annunci soci */}
                {myListings.length > 0 && (
                    <Link to="/app/investimento" className="mt-6 bento p-5 flex items-center gap-4 hover:border-acid transition group" data-testid="badge-active-listing">
                        <div className="h-10 w-10 rounded-full bg-acid/20 flex items-center justify-center shrink-0">
                            <Briefcase className="h-5 w-5 text-acid" />
                        </div>
                        <div className="flex-1">
                            <div className="font-grotesk font-medium">
                                {myListings.length === 1
                                    ? `Hai un'inserzione attiva sulla Bacheca annunci soci — Quota ${myListings[0].quota_category}`
                                    : `Hai ${myListings.length} inserzioni attive sulla Bacheca annunci soci`}
                            </div>
                            <div className="text-zinc-500 text-xs mt-1">Gestisci vendita e ritiri da &quot;Il mio investimento&quot;</div>
                        </div>
                        <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-acid transition" />
                    </Link>
                )}

                {/* Quick actions */}
                <div className="mt-12">
                    <span className="label-tag">— Azioni rapide</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <QuickAction to="/app/profilo" icon={User} title="Il mio profilo" subtitle="Aggiorna i contatti" testid="qa-profilo" />
                        <QuickAction to="/app/documenti" icon={FileText} title="Documenti" subtitle="Verbali, certificati, comunicazioni" testid="qa-docs" />
                        <QuickAction to="/app/investimento" icon={TrendingUp} title="Il mio investimento" subtitle="Posizione e vendita quote" testid="qa-investimento" />
                        <QuickAction to="/app/mercato" icon={Briefcase} title="Bacheca annunci soci" subtitle="Vetrina cessioni tra soci" testid="qa-mercato" />
                    </div>
                </div>

                {/* Recent docs */}
                <div className="mt-12">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <span className="label-tag">— Ultimi documenti</span>
                            <h2 className="font-display text-3xl tracking-tight mt-2">Per te</h2>
                        </div>
                        <Link to="/app/documenti" className="text-xs uppercase tracking-widest font-grotesk text-bayside hover:text-white" data-testid="see-all-docs">Vedi tutti</Link>
                    </div>
                    {docs.length === 0 ? (
                        <div className="bento p-10 text-center text-zinc-500" data-testid="empty-docs">Nessun documento ancora disponibile.</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {docs.slice(0, 4).map(d => (
                                <Link key={d.id} to="/app/documenti" className="bento p-5 flex items-center justify-between hover:border-bayside/50 transition" data-testid={`doc-tile-${d.id}`}>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-zinc-500" />
                                            <span className="font-grotesk font-medium">{d.title}</span>
                                            {!d.is_read && <span className="bg-gradient-tropical text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NUOVO</span>}
                                        </div>
                                        <div className="text-zinc-500 text-xs mt-2 uppercase tracking-widest font-grotesk">
                                            {d.category} · {new Date(d.published_at).toLocaleDateString("it-IT")}
                                        </div>
                                    </div>
                                    <ArrowUpRight className="h-5 w-5 text-zinc-600" />
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
}

function ActionItemsPanel({ items }) {
    if (items.length === 0) {
        return (
            <div className="mt-10 bento p-6 flex items-center gap-4" data-testid="actions-all-clear">
                <div className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                </div>
                <div>
                    <div className="font-display text-lg">Tutto in ordine.</div>
                    <p className="text-zinc-400 text-sm mt-1">Nessuna azione richiesta. Le tue informazioni sono aggiornate.</p>
                </div>
            </div>
        );
    }
    return (
        <div className="mt-10" data-testid="actions-panel">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <span className="label-tag">— Cose da fare</span>
                    <h2 className="font-display text-3xl tracking-tight mt-2">
                        {items.length} {items.length === 1 ? "azione" : "azioni"} <span className="font-serif-italic text-gradient-tropical">in sospeso</span>.
                    </h2>
                </div>
                <span className="text-xs uppercase tracking-widest font-grotesk text-zinc-500" data-testid="actions-count">
                    {items.filter(i => i.priority === "high").length > 0 && (
                        <span className="text-vermillion mr-3">● {items.filter(i => i.priority === "high").length} priorità alta</span>
                    )}
                </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="actions-grid">
                {items.map(item => <ActionItemCard key={item.id} item={item} />)}
            </div>
        </div>
    );
}

function ActionItemCard({ item }) {
    const Icon = item.icon;
    const priorityBorder = item.priority === "high" ? "border-l-vermillion" : item.priority === "medium" ? "border-l-magenta" : "border-l-bayside";
    const priorityIcon = item.priority === "high" ? "text-vermillion bg-vermillion/15" : item.priority === "medium" ? "text-magenta bg-magenta/15" : "text-bayside bg-bayside/15";
    return (
        <Link
            to={item.to}
            data-testid={item.testid}
            className={`bento p-5 border-l-4 ${priorityBorder} hover:border-bayside/40 transition flex flex-col`}
        >
            <div className="flex items-start gap-4">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${priorityIcon}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display text-lg leading-tight">{item.title}</h3>
                        <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-white shrink-0 mt-1" />
                    </div>
                    <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{item.description}</p>
                    {item.percent !== undefined && (
                        <div className="mt-3" data-testid={`${item.testid}-progress`}>
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mb-1.5">
                                <span>Completamento profilo</span>
                                <span className="text-white">{item.percent}%</span>
                            </div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[linear-gradient(90deg,#ed4735_0%,#df46e7_50%,#653fcd_100%)] transition-all"
                                    style={{ width: `${item.percent}%` }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-grotesk text-bayside">
                        {item.cta} <ArrowUpRight className="h-3 w-3" />
                    </div>
                </div>
            </div>
        </Link>
    );
}

function KpiBig({ title, value, sub, breakdown, testid }) {
    return (
        <div className="bento col-span-12 lg:col-span-6 grain p-10" data-testid={testid}>
            <span className="label-tag">{title}</span>
            <div className="font-serif-italic text-7xl lg:text-8xl text-gradient-tropical leading-none mt-6 tracking-tight">{value}</div>
            {sub && <p className="text-zinc-500 mt-4 text-sm font-grotesk uppercase tracking-widest">{sub}</p>}
            {breakdown && breakdown.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid={`${testid}-breakdown`}>
                    {breakdown.map(([cat, val]) => (
                        <div
                            key={cat}
                            className="border border-zinc-800 bg-black/40 px-3 py-1.5 font-grotesk text-xs"
                            data-testid={`${testid}-cat-${cat}`}
                        >
                            <span className="text-zinc-500 uppercase tracking-widest mr-2">Quota {cat}</span>
                            <span className="text-white font-medium">{val.toLocaleString("it-IT", { maximumFractionDigits: 2 })}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
function KpiSmall({ icon: Icon, title, value, color, testid }) {
    const cl = color === "bayside" ? "text-bayside" : color === "magenta" ? "text-magenta" : "text-white";
    return (
        <div className="bento col-span-6 lg:col-span-3 p-8" data-testid={testid}>
            <Icon className={`h-6 w-6 ${cl} mb-6`} />
            <div className="font-display text-6xl tracking-tighter">{value}</div>
            <div className="text-zinc-500 text-xs uppercase tracking-widest font-grotesk mt-2">{title}</div>
        </div>
    );
}
function QuickAction({ to, icon: Icon, title, subtitle, testid }) {
    return (
        <Link to={to} className="bento p-6 group hover:border-bayside/40 transition" data-testid={testid}>
            <div className="flex items-start justify-between">
                <Icon className="h-6 w-6 text-bayside" />
                <ArrowUpRight className="h-4 w-4 text-zinc-600 group-hover:text-bayside transition" />
            </div>
            <div className="font-grotesk font-semibold mt-6">{title}</div>
            <div className="text-zinc-500 text-xs mt-1">{subtitle}</div>
        </Link>
    );
}
